(ns hai.tasks.data
  "Shared task state, events, subscriptions, and domain helpers."
  (:require
   [clojure.string :as str]
   [hai.entity.draft :as draft]
   [hai.entity.staging :as staging]
   [hai.sharing.helper :as sharing]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.eql :as eql]
   [hai.tasks.urgency :as urgency]
   [hai.ui.request :as request]
   [hai.ui.rf-sugar :refer [defevent defevent-fx defsub]]))

(defn- start-of-today []
  (doto (js/Date.) (.setHours 0 0 0 0)))

(defn task-bucket
  "Bucket a task by its `:task/urgency`. The three lanes map to their buckets;
   everything else — none, and a task with no urgency set at all — lands in
   :none."
  [task]
  (case (:task/urgency task)
    :task.urgency/now   :now
    :task.urgency/next  :next
    :task.urgency/later :later
    :none))

(defn staged-bucket
  "The bucket a task the composer just saved lands in. Its payload carries a
   bare `:urgency`, not the `:task/urgency` [[task-bucket]] reads."
  [{:keys [urgency]}]
  (task-bucket {:task/urgency urgency}))

(defn assignee-email
  "First email of a task's assignee; the attr may be a collection."
  [assignee]
  (let [email (:user/email assignee)]
    (if (coll? email) (first email) email)))

(defn assignee-name [assignee]
  (or (not-empty (:user/full-name assignee))
      (not-empty (assignee-email assignee))))

(defn contact-name
  "Display name for a contact picked in the assignee picker. That's the shape a
   staged, not-yet-saved assignee has — `task/assign` is what turns it into the
   user `assignee-name` reads."
  [contact]
  (or (not-empty (:contact/full-name contact))
      (not-empty (first (:contact/email contact)))))

(defn picked-assignee?
  "True when `assignee` is a fresh pick the picker staged and `task/assign` can
   apply — either a contact (`:contact/uuid`) or a raw email typed for someone
   not in the requester's contacts (`:user/email` with no `:user/uuid`). An
   untouched picker still holds the task's resolved assignee, which carries a
   `:user/uuid`; that's left alone."
  [assignee]
  (boolean (or (:contact/uuid assignee)
               (and (:user/email assignee) (not (:user/uuid assignee))))))

(defn assignee->assign-params
  "`task/assign` params for a staged pick — a contact by uuid, or a raw email.
   A typed email carries `:contact/add?` (the picker's \"Add contact\" checkbox):
   whether the backend should also save the address to the requester's personal
   contacts. Defaults to true when the pick predates the checkbox."
  [assignee]
  (if-let [contact-uuid (:contact/uuid assignee)]
    {:contact/uuid contact-uuid}
    {:user/email (assignee-email assignee)
     :contact/add? (get assignee :contact/add? true)}))

(defn assignee-me? [current-user assignee]
  (= (:user/uuid current-user)
     (:user/uuid assignee)))

(defn can-complete?
  "Whether `current-user` may stamp the task complete or lift the stamp: its
   owner or its assignee. An admin who is neither edits and shares, but does
   not stamp."
  [current-user task]
  (or (sharing/owner? task)
      (assignee-me? current-user (:task/assigned-to task))))

(defn can-assign?
  "Whether the requester may set or clear the task's assignee. Assigning
   grants the assignee admin, so it takes the sharing right: owner or admin.
   A contributor edits the rest of the task."
  [task]
  (sharing/can-share? task))

(defn assignee-initials [assignee]
  (let [from-name (->> (str/split (or (:user/full-name assignee) "") #"\s+")
                       (keep first)
                       (take 2)
                       (apply str))
        email (assignee-email assignee)]
    (cond
      (seq from-name) (str/upper-case from-name)
      (seq email)     (str/upper-case (subs email 0 1)))))

(def ^:private user-eql
  "The user attributes a tasks surface reads, fetched as part of the tasks loads,
   and alone only when another tab changed them. The web-app's `/me` already puts
   them on `:auth/identity`; this is how the extension's — minted from the JWT
   alone — gets them, and how a copy that went stale in another client is
   refreshed. Keep this list and [[user-config-loaded]]'s merge target the same
   `:auth/identity` keys, so adding an attribute is one line here."
  [:user-config.tasks/default-urgency])

;; Home's "My Tasks" mini-app loads the tasks the current user owns or is
;; assigned: every open one, plus any completed earlier today so the day's
;; finished items still show (struck through). `:user/current-tasks` returns
;; both in a single request, newest first.
(defevent-fx ::load-owned-or-assigned-tasks [_ & [extra-success-fx extra-error-fx]]
  {:resolve {:query (into [{(list :user/current-tasks {:completed-since (start-of-today)})
                            eql/task-eql}]
                          user-eql)
             :store-in [::load-result]
             :transform-fn (fn [resp] (get resp :user/current-tasks []))
             :on-success-fx (into [[:dispatch [::loaded]]
                                   [:dispatch [::user-config-loaded '?body]]]
                                  extra-success-fx)
             :on-error-fx (vec extra-error-fx)}})

;; The user attributes riding the response, onto the identity — the one place
;; every surface reads them from.
(defevent ::user-config-loaded [db response]
  (update db :auth/identity merge (select-keys response user-eql)))

(defevent-fx ::load-user-config [_ _]
  {:resolve {:query user-eql
             :on-success-fx [[:dispatch [::user-config-loaded '?body]]]}})

;; The full /app/tasks page loads every task the user can see — their own and
;; ones shared with them — but only the open ones; completed tasks live in the
;; page's separate Completed archive. Newest first.
(defevent-fx ::load-all-my-tasks [_ _]
  {:resolve {:query (into [{(list :user.tasks/table
                                  {:pagination {:per-page 500
                                                :attrs [:task/created-at]
                                                :direction :desc}
                                   :filters {:task/open? true}})
                            [{:collection/items eql/task-eql}]}]
                          user-eql)
             :store-in [::load-result]
             :transform-fn (fn [resp] (get-in resp [:user.tasks/table :collection/items] []))
             :on-success-fx [[:dispatch [::loaded]]
                             [:dispatch [::user-config-loaded '?body]]]}})

;; Both loads land in the same `::tasks` slice — the one both surfaces read.
(defevent ::loaded [db _]
  (-> db
      (assoc ::tasks (get db ::load-result []))
      (dissoc ::load-result)))

(defn- error-text
  "User-facing toast text for a failed save, from the `:on-error-fx` body."
  [response]
  (if (= :activity/url (-> response :com.wsscode.pathom3.error/error-data :attr))
    "Could not save task. Check that your links are valid URLs."
    "Could not save task. Try again."))

(defn- picked->assignee
  "Optimistic `:task/assigned-to` shape from a staged pick — a contact or a raw
   email. The same `:user/full-name` + `:user/email` shape the row reads, so the
   just-created task shows the assignee right away. It has no `:user/uuid` until
   `task/assign` resolves and swaps in the real user."
  [picked]
  (if (:contact/uuid picked)
    {:user/full-name (:contact/full-name picked)
     :user/email (:contact/email picked)}
    {:user/email (:user/email picked)}))

(def board-collection-id
  "Collection id of the tasks-page :query kanban board ([HAI-001-04]). Owned here,
  in the data layer, so data-layer success handlers (e.g. `::create-resolved`)
  can reconcile the board without a circular dependency on the page namespace;
  `hai.tasks.page` reads it back for the board's `:id` and its per-task
  re-resolves."
  ::board)

(defn- staged-chips
  "The composer stages whole entities; a cached task only carries the chip shape
   `task-eql` asks for. Empty families are dropped, so this merges onto a
   task without blanking what it already has. A tag typed fresh has no uuid
   until the server makes one — its name carries the chip until the re-resolve."
  [{:keys [contacts notes tasks tags]}]
  (into {}
        (remove (comp empty? val))
        {:task/contacts (mapv staging/contact-chip contacts)
         :task/notes (mapv staging/note-chip notes)
         :task/tasks (mapv staging/task-chip tasks)
         :task/tags (mapv staging/tag-chip tags)}))

(defn edit-draft-id
  "The draft behind one task's in-place editor. Per task, so two open rows
   don't edit each other's."
  [task-uuid]
  [::edit-draft task-uuid])

(def composer-draft-id
  "The draft behind the Add-a-task composer. One at a time, and the desktop and
   mobile home cards are the same one."
  ::composer-draft)

(def editor-draft-id
  "The draft behind the mini-app cards' editor ([[::open-editor]]). One at a
   time, and apart from the board's per-task drafts, so the two never seed or
   discard each other's."
  ::editor-draft)

;; Which board cards show the composer in place of the card, so a save can put
;; the card back once it lands. The host hands over the task as filed
;; ([[hai.tasks.form/from-task]]) rather than this reading it back: only the
;; host knows which copy of the task its surface is showing.
(defevent ::start-edit [db task-uuid staged]
  (-> db
      (update ::editing-uuids (fnil conj #{}) task-uuid)
      (draft/seed (edit-draft-id task-uuid) staged)))

(defevent ::stop-edit [db task-uuid]
  (-> db
      (update ::editing-uuids disj task-uuid)
      (draft/discard (edit-draft-id task-uuid))))

;; Cancel: the draft goes back to the task as last saved, which the editor is
;; already showing.
(defevent ::cancel-edit [db task-uuid staged]
  (draft/seed db (edit-draft-id task-uuid) staged))

(defsub ::editing? [db task-uuid]
  (contains? (get db ::editing-uuids #{}) task-uuid))

;; The mini-app cards open one task at a time over the whole card, in place of
;; the list. The editor stays up across a save; Back, or deleting the task, puts
;; the list back.
(defevent ::open-editor [db task-uuid staged]
  (-> db
      (assoc ::editor-task-uuid task-uuid)
      (draft/seed editor-draft-id staged)))

(defevent ::close-editor [db _]
  (-> db
      (dissoc ::editor-task-uuid)
      (draft/discard editor-draft-id)))

(defsub ::editor-task-uuid [db]
  (get db ::editor-task-uuid))

;; The open task as the slice has it now; nil once it leaves the slice.
(defsub ::editor-task
  [task-uuid <- [::editor-task-uuid]
   tasks <- [::list]]
  (when task-uuid
    (some #(when (= task-uuid (:task/uuid %)) %) tasks)))

;; ---------------------------------------------------------------------------
;; Default urgency — the lane a new task starts in, picked from the ＋ pill in
;; the tasks header ([[hai.tasks.add-pill]]) and kept on the user, so it holds
;; across sessions and surfaces. It lives on `:auth/identity` and nowhere else:
;; `/me` puts it there on the web-app, the tasks load ([[user-eql]]) does on the
;; extension and refreshes it on both, and the pick below writes the same key
;; and has the other open tabs refetch it. With no pick made, a new task starts
;; with no urgency.
;; ---------------------------------------------------------------------------

(defn default-urgency
  "The urgency a new task starts at, for events that seed a composer."
  [db]
  (or (get-in db [:auth/identity :user-config.tasks/default-urgency])
      urgency/none))

(defsub ::default-urgency [db]
  (default-urgency db))

(defevent-fx ::set-default-urgency
  [{:keys [db]} value]
  {:db (assoc-in db [:auth/identity :user-config.tasks/default-urgency] value)
   :resolve {:query `[(~'user/update
                       ~{:user/uuid (get-in db [:auth/identity :user/uuid])
                         :user-config.tasks/default-urgency value})]
             :on-success-fx [[:dispatch [:sync/changed {:scope :user}]]]}})

;; The home card's Add-a-task composer, open until its create lands.
(defevent ::open-composer [db staged]
  (-> db
      (assoc ::composer-open? true)
      (draft/seed composer-draft-id staged)))

(defevent ::close-composer [db _]
  (-> db
      (dissoc ::composer-open?)
      (draft/discard composer-draft-id)))

(defsub ::composer-open? [db]
  (get db ::composer-open? false))

(defn- task-link-action
  "The success toast's \"View task\" link, from a host's `:task-link` —
   `{:href-fn (fn [uuid] url) :target \"_self\" :label \"View task\"}`. The host
   owns the URL shape — a client-side route in the web app, an absolute platform
   URL in the extension — since this shared code can't know either. nil without
   one, which leaves the toast plain text.

   Hosts that are already showing the task pass nothing: the task detail page's
   own save would be linking to the page you are reading it on."
  [{:keys [href-fn target label]} task-uuid]
  (when href-fn
    {:label  (or label "View task")
     :href   (href-fn task-uuid)
     :target target}))

;; Every task mutation carries this on success, so the other open tabs refetch;
;; each surface handles `:sync/changed`.
(def ^:private tasks-changed-fx
  [:dispatch [:sync/changed {:scope :tasks}]])

;; `:request-id` names the request, for the composer's in-flight state;
;; `:success-fx` rides the create's response — the host closing its composer.
;; `:task-link`, when supplied, links the success toast to the new task
;; ([[task-link-action]]).
(defevent-fx ::create
  [{:keys [db]} {:keys [title description urgency deadline urls assignee
                        contacts notes tasks tags request-id success-fx
                        task-link] :as staged}]
  (let [new-uuid (random-uuid)
        now (js/Date.)
        deadline-params (deadline/value->params deadline)
        link-urls (vec (distinct urls))
        ;; A fresh pick (a contact, or a raw email) reassigns away from the
        ;; creator; an untouched picker still holding the creator leaves the
        ;; default assign-to-me alone.
        assignee-picked (when (picked-assignee? assignee) assignee)
        contact-uuids (mapv :contact/uuid contacts)
        note-uuids (mapv :note/uuid notes)
        task-uuids (mapv :task/uuid tasks)
        tag-uuids (vec (keep :group/uuid tags))
        tag-names (vec (keep :tag/name tags))
        task (merge (cond-> {:task/uuid new-uuid
                             :task/title title
                             :task/created-at now
                             :task/updated-at now
                             :task/assigned-to (if assignee-picked
                                                 (picked->assignee assignee-picked)
                                                 (select-keys (:auth/identity db)
                                                              [:user/uuid :user/full-name :user/email]))
                             :user.entity/permissions [:permission.type/admin]}
                      (seq description) (assoc :task/description description)
                      urgency (assoc :task/urgency urgency)
                      deadline-params (merge deadline-params)
                      (seq link-urls) (assoc :task/activities
                                             (mapv (fn [url] {:activity/url url}) link-urls)))
                    ;; Embed the staged links and tags so the just-created card
                    ;; shows their chips instantly, before any re-resolve
                    ;; ([HAI-001-10]).
                    (staged-chips staged))
        params (cond-> {:task/title title
                        :task/uuid new-uuid}
                 (seq description) (assoc :task/description description)
                 urgency (assoc :task/urgency urgency)
                 (seq link-urls) (assoc :activity/urls link-urls)
                 deadline-params (merge deadline-params)
                 ;; Linked entities picked in the create composer; the backend
                 ;; `task/create` accepts these additively, same as `task/update`.
                 (seq contact-uuids) (assoc :task/contact-uuids contact-uuids)
                 (seq note-uuids) (assoc :task/note-uuids note-uuids)
                 (seq task-uuids) (assoc :task/task-uuids task-uuids)
                 (seq tag-uuids) (assoc :task/tag-uuids tag-uuids)
                 (seq tag-names) (assoc :task/tag-names tag-names))]
    (when-not (and request-id (request/in-flight? db request-id))
      {:db (cond-> (-> db
                       (update ::tasks #(into [task] %))
                       (assoc ::highlighted-uuid new-uuid))
             request-id (request/mark-in-flight request-id))
       ;; Optimistically prepend to the :query board too ([HAI-001-10]): it renders
       ;; from `:collections`, not the `::tasks` slice above, so without this the
       ;; new card wouldn't show until the create re-resolves. Instant feedback,
       ;; consistent with the other per-task board actions ([HAI-001-07]); the
       ;; success re-resolve reconciles, and `::create-failed` rolls it back.
       ;; Mount-guarded inside `:table/add-row` (creation also fires off-board).
       :fx [[:dispatch [:table/add-row board-collection-id task]]]
       ;; Briefly flag the just-added row so the card can flash it, then let it
       ;; settle. Keyed by uuid so only this task lights up, not every row on load.
       :dispatch-later {:ms 2000 :dispatch [::clear-highlight]}
       :resolve {:query [{(list 'task/create params) [:task/uuid]}]
                 :request-id request-id
                 :on-success-fx (into [[:dispatch [::create-resolved new-uuid assignee-picked
                                                   (task-link-action task-link new-uuid)]]
                                       tasks-changed-fx]
                                      success-fx)
                 :on-error-fx [[:dispatch [::create-failed new-uuid '?body]]]}})))

(defevent ::clear-highlight [db _]
  (dissoc db ::highlighted-uuid))

(defevent-fx ::create-failed [{:keys [db]} new-uuid response]
  {:db (update db ::tasks
               (fn [tasks]
                 (filterv #(not= new-uuid (:task/uuid %)) tasks)))
   ;; Roll the optimistic board row back too ([HAI-001-10]), mount-guarded like
   ;; the add.
   :fx [[:dispatch [:table/remove-row board-collection-id :task/uuid new-uuid]]
        [:dispatch [:toast/error {:text (error-text response)}]]]})

(defevent-fx ::create-resolved
  [_ new-uuid assignee-picked toast-action]
  ;; The task now exists, so a picked assignee can be applied — `task/assign`
  ;; reassigns it off the creator and grants the assignee access. Its own
  ;; resolver swaps the optimistic contact for the real user.
  (let [;; `::create` optimistically prepended the new card to the :query board
        ;; ([HAI-001-10]); this re-resolve is the reconciling authority that
        ;; replaces the optimistic row with server truth (the [HAI-001-07]
        ;; patch-then-reconcile pattern). Mount-guarded: creation also fires
        ;; from the mobile add-sheet / universal bar, where the board isn't
        ;; rendered.
        reconcile-board [:dispatch [:table/resolve-if-mounted board-collection-id]]]
    {:fx (cond-> [[:dispatch [:toast/success (cond-> {:text "Task added"}
                                               toast-action (assoc :action toast-action))]]
                  ;; A new task shifts the board's total + its assignee's count,
                  ;; so refresh the facet sidebar (mount-guarded in ::load-facets).
                  [:dispatch [::load-facets]]]
           ;; No reassignment: reconcile now (the optimistic row already
           ;; matches server truth bar server-normalised fields).
           (not assignee-picked) (conj reconcile-board)
           ;; A picked assignee resolves *after* this via `task/assign`; carry
           ;; the reconcile through it instead, so the board holds the
           ;; optimistic (picked-assignee) row until the final user lands -
           ;; reconciling now would flash the pre-assign creator over it.
           assignee-picked (conj [:dispatch [::assign new-uuid assignee-picked
                                             [reconcile-board]]]))}))

(defn- task-ident
  "Entity-store key for a task, matching `hai.entity.helpers/->ident` so the
  detail page reads the same copy through the shared `:entity` subscription."
  [task-uuid]
  [:task/uuid (uuid (str task-uuid))])

(defn- patch-task
  "Update one task by uuid in the `::tasks` slice and, when present, the shared
  entity store, so optimistic edits show on the list and the detail page alike."
  [db task-uuid f]
  (let [patch-in (fn [tasks]
                   (mapv (fn [task]
                           (if (= task-uuid (:task/uuid task)) (f task) task))
                         tasks))]
    (cond-> (update db ::tasks patch-in)
      (get-in db (task-ident task-uuid)) (update-in (task-ident task-uuid) f))))

;; Completion is optimistic: the patch alone moves the task between Home's open
;; and struck states (and off the page's open buckets), so no reload is needed.
;; A caller with its own dependent state — e.g. the tasks page's Completed
;; archive — passes it as `extra-success-fx`.
(defevent-fx ::complete [{:keys [db]} task-uuid & [extra-success-fx]]
  {:db (patch-task db task-uuid #(assoc % :task/completed-at (js/Date.)))
   :resolve {:query [{(list 'task/complete {:task/uuid task-uuid})
                      [:task/uuid :task/completed-at]}]
             ;; Completing drops the task off the open board, shifting the facet
             ;; total + its assignee's count; refresh the sidebar.
             :on-success-fx (conj (vec extra-success-fx)
                                  [:dispatch [::load-facets]]
                                  tasks-changed-fx)}})

(defevent-fx ::uncomplete [{:keys [db]} task-uuid & [extra-success-fx]]
  {:db (patch-task db task-uuid #(dissoc % :task/completed-at))
   :resolve {:query [{(list 'task/uncomplete {:task/uuid task-uuid}) [:task/uuid]}]
             ;; Un-completing returns the task to the open board, shifting the
             ;; facet total + its assignee's count; refresh the sidebar.
             :on-success-fx (conj (vec extra-success-fx)
                                  [:dispatch [::load-facets]]
                                  tasks-changed-fx)}})

(defevent-fx ::delete [{:keys [db]} task-uuid & [extra-success-fx]]
  (let [deleted (some #(when (= task-uuid (:task/uuid %)) %) (get db ::tasks []))]
    {:db (update db ::tasks (fn [tasks] (filterv #(not= task-uuid (:task/uuid %)) tasks)))
     ;; No join: `task/delete` returns `{}`, so asking it for `:task/uuid` back
     ;; leaves Pathom no path to it and reports the whole delete as failed.
     :resolve {:query [(list 'task/delete {:task/uuid task-uuid})]
               :on-success-fx (conj (vec extra-success-fx)
                                    [:dispatch [:toast/success {:text "Task deleted"}]]
                                    ;; Removing a task shifts the board total + the
                                    ;; assignee's count; refresh the facet sidebar.
                                    [:dispatch [::load-facets]]
                                    tasks-changed-fx)
               :on-error-fx [[:dispatch [::delete-failed deleted]]]}}))

;; Both loads sort newest first, so the restored task goes back by created-at
;; rather than to the head of the list it was cut from.
(defevent-fx ::delete-failed [{:keys [db]} task]
  {:db (cond-> db
         task (update ::tasks
                      (fn [tasks]
                        (vec (sort-by :task/created-at
                                      #(compare %2 %1)
                                      (conj (vec tasks) task))))))
   :fx [[:dispatch [:table/resolve-if-mounted board-collection-id]]
        [:dispatch [:toast/error {:text "Could not delete task. Try again."}]]]})

(defevent-fx ::update
  [{:keys [db]} {:keys [task-uuid title description urgency deadline clear-deadline?
                        clear-assignee? urls
                        tag-uuids tag-names untag-uuids contact-uuids note-uuids task-uuids
                        request-id]}
   & [extra-success-fx]]
  (let [prev-task (some #(when (= task-uuid (:task/uuid %)) %)
                        (get db ::tasks []))
        deadline-params (deadline/value->params deadline)
        link-urls (vec (distinct urls))
        patch (cond-> {}
                clear-assignee? (assoc :task/assigned-to nil)
                (some? title) (assoc :task/title title)
                (some? description) (assoc :task/description description)
                (some? urgency) (assoc :task/urgency urgency)
                deadline-params (merge deadline-params)
                clear-deadline? (assoc :task/deadline-at nil
                                       :task/deadline-all-day? nil
                                       :task/deadline-timezone nil)
                (some? urls) (assoc :task/activities
                                    (mapv (fn [url] {:activity/url url}) link-urls)))
        rollback (into {} (map (fn [k] [k (get prev-task k)])) (keys patch))
        ;; Tags and entity links carry no optimistic patch — they are not on the
        ;; cached card — so they ride the params only, and the re-resolve is what
        ;; shows them.
        params (cond-> (merge {:task/uuid task-uuid}
                              (dissoc patch :task/activities :task/assigned-to))
                 clear-deadline? (assoc :clear-deadline? true)
                 clear-assignee? (assoc :clear-assignee? true)
                 (some? urls) (assoc :activity/urls link-urls)
                 (seq tag-uuids) (assoc :task/tag-uuids (vec tag-uuids))
                 (seq tag-names) (assoc :task/tag-names (vec tag-names))
                 (seq untag-uuids) (assoc :task/untag-uuids (vec untag-uuids))
                 (seq contact-uuids) (assoc :task/contact-uuids (vec contact-uuids))
                 (seq note-uuids) (assoc :task/note-uuids (vec note-uuids))
                 (seq task-uuids) (assoc :task/task-uuids (vec task-uuids)))]
    {:db (patch-task db task-uuid #(merge % patch))
     :resolve {:query [{(list 'task/update params) [:task/uuid]}]
               :request-id request-id
               :on-success-fx (conj (vec extra-success-fx) tasks-changed-fx)
               :on-error-fx [[:dispatch [::update-failed task-uuid rollback '?body]]]}}))

(defn- unlinked-uuids
  "The uuids the composer dropped: what the task has linked, minus what came
   back staged."
  [id-key linked staged]
  (let [staged-uuids (set (keep id-key staged))]
    (vec (remove staged-uuids (keep id-key linked)))))

(defn- added-uuids
  "The uuids the composer newly staged: what came back staged, minus what the
   task already has. Tags need this diff — `task/update` asserts the requester
   owns every tag uuid it is sent, so re-sending one that is merely riding along
   rejects the whole save on a task shared by someone else."
  [id-key linked staged]
  (let [linked-uuids (set (keep id-key linked))]
    (vec (remove linked-uuids (keep id-key staged)))))

;; Apply the composer's staged fields to an existing task. The scalars, the url
;; set and the tag reconciliation ride one `task/update`, which also attaches
;; linked entities; dropping a linked entity needs its own `task/remove-*`, and
;; a freshly picked assignee still goes through `task/assign`.
;; `:success-fx` rides the response — the host's re-resolve; the request is
;; `[::save-edit task-uuid]`, for the composer's in-flight state.
(defevent-fx ::save-edit
  [{:keys [db]} task {:keys [title description urgency deadline urls contacts
                             notes tasks tags spaces assignee success-fx task-link] :as staged}]
  ;; TODO(evgheni): a save is several parallel mutations and can half-apply.
  ;; Fold them into one `task/update` transaction before tasks auto-save.
  (let [task-uuid (:task/uuid task)
        request-id [::save-edit task-uuid]
        tags (staging/reconcile-tags (:task/tags task) tags)
        ;; Spaces file through their own mutations, not `task/update`; diff the
        ;; staged set against what the task is in and add/remove the deltas.
        added-spaces (added-uuids :space/uuid (:entity/spaces task) spaces)
        dropped-spaces (unlinked-uuids :space/uuid (:entity/spaces task) spaces)
        assignee-picked? (picked-assignee? assignee)
        ;; An untouched picker still holds the task's assignee, so an empty one
        ;; on a task that has someone is the chip's ✕, not "didn't touch it".
        assignee-cleared? (boolean (and (:task/assigned-to task) (nil? assignee)))
        dropped-contacts (unlinked-uuids :contact/uuid (:task/contacts task) contacts)
        dropped-notes (unlinked-uuids :note/uuid (:task/notes task) notes)
        dropped-tasks (unlinked-uuids :task/uuid (:task/tasks task) tasks)
        tag-uuids (added-uuids :group/uuid (:task/tags task) tags)
        tag-names (vec (keep :tag/name tags))
        untag-uuids (unlinked-uuids :group/uuid (:task/tags task) tags)
        ;; Links and tags carry no patch of their own through `::update` — they
        ;; are not scalars on the card — so the staged chips land here, or the
        ;; surfaces that don't re-resolve would show yesterday's links.
        chips (staged-chips (assoc staged :tags tags))
        ;; The editor stays up across a save — it is the ✕ that closes it, and
        ;; the composer diffs its next save against the task as the save left
        ;; it. Only the create composers close on success, from their host's
        ;; own `:success-fx`.
        success-fx (conj (vec success-fx)
                         [:dispatch [:toast/success
                                     (cond-> {:text "Task saved"}
                                       task-link
                                       (assoc :action (task-link-action task-link task-uuid)))]])]
    (when-not (request/in-flight? db request-id)
      {:db (-> (patch-task db task-uuid
                           (fn [cached]
                             (-> cached
                                 (assoc :task/contacts [] :task/notes [] :task/tasks []
                                        :task/tags [])
                                 (merge chips))))
               (request/mark-in-flight request-id))
       :fx (-> [[:dispatch [::update {:task-uuid task-uuid
                                      :request-id request-id
                                      :title title
                                      :description description
                                      :urgency urgency
                                      :deadline deadline
                                      :clear-deadline? (and (:task/deadline-at task)
                                                            (nil? (:date deadline)))
                                      :clear-assignee? assignee-cleared?
                                      :urls urls
                                      :tag-uuids tag-uuids
                                      :tag-names tag-names
                                      :untag-uuids untag-uuids
                                      ;; Deltas, like the tags above: `task/update`
                                      ;; asserts the requester owns every contact
                                      ;; and can read every note/task it is sent.
                                      ;; A task shared with you can carry links to
                                      ;; things you cannot open — re-sending one
                                      ;; that is merely riding along rejects the
                                      ;; whole save.
                                      :contact-uuids (added-uuids :contact/uuid (:task/contacts task) contacts)
                                      :note-uuids (added-uuids :note/uuid (:task/notes task) notes)
                                      :task-uuids (added-uuids :task/uuid (:task/tasks task) tasks)}
                            ;; The caller's re-resolve rides exactly one mutation.
                            ;; A picked assignee lands last through `task/assign`,
                            ;; so it carries the re-resolve instead — on both, the
                            ;; earlier round-trip can land after the later one and
                            ;; flash the pre-assign state.
                            (when-not assignee-picked? success-fx)]]]
               (into (for [contact-uuid dropped-contacts]
                       [:dispatch [::remove-contact {:task/uuid task-uuid
                                                     :contact/uuid contact-uuid}]]))
               (into (for [note-uuid dropped-notes]
                       [:dispatch [::remove-note-link {:task/uuid task-uuid
                                                       :note/uuid note-uuid}]]))
               (into (for [linked-uuid dropped-tasks]
                       [:dispatch [::remove-task-link {:task/uuid task-uuid
                                                       :task/linked-uuid linked-uuid}]]))
               (into (for [space-uuid added-spaces]
                       [:dispatch [::add-to-space task-uuid space-uuid]]))
               (into (for [space-uuid dropped-spaces]
                       [:dispatch [::remove-from-space task-uuid space-uuid]]))
               (cond-> assignee-picked?
                 (conj [:dispatch [::assign task-uuid assignee success-fx]])))})))

(defsub ::contacts [db]
  (get db ::contacts))

;; The search text that produced whatever is currently in `::contacts`. The
;; picker compares it against the live input to tell "search still in flight"
;; (query ≠ input) from "search done, nothing matched" (query = input), so it
;; doesn't flash "No matching contacts" between a keystroke and its response.
(defsub ::contacts-query [db]
  (get db ::contacts-query))

;; Server-side contact search backing the assignee picker. `:contact/search`
;; filters in Datomic by name/email and returns nothing for a blank query, so
;; the picker searches as-you-type rather than fetching the whole list once.
;; Contacts without an email are dropped here — the `task/assign` mutation
;; resolves the assignee by email, so an email-less contact can't be assigned.
(defevent-fx ::search-contacts [_ text]
  {:resolve {:query [{(list :contact/search {:contact/search-text text})
                      [:contact/uuid :contact/full-name :contact/email]}]
             :store-in [::contacts-load-result]
             :transform-fn (fn [resp]
                             (->> resp
                                  :contact/search
                                  (filter #(seq (:contact/email %)))))
             :on-success-fx [[:dispatch [::loaded-contacts text]]]}})

;; Commit results + the query that produced them together, so a consumer never
;; sees the new list paired with a stale query (or vice versa).
(defevent ::loaded-contacts [db text]
  (-> db
      (assoc ::contacts (get db ::contacts-load-result []))
      (assoc ::contacts-query text)
      (dissoc ::contacts-load-result)))

(defevent ::clear-contacts [db _]
  (dissoc db ::contacts ::contacts-query ::contacts-load-result))

(defevent-fx ::assign [{:keys [db]} task-uuid assignee & [extra-success-fx]]
  ;; Optimistic: the picked contact/email shows as the assignee right away —
  ;; the same shape ::create embeds — on the slice, the entity store, and the
  ;; :query board. `task/assign`'s response swaps in the real user, and an
  ;; error restores the previous assignee.
  (let [optimistic (picked->assignee assignee)
        previous (:task/assigned-to (or (some #(when (= task-uuid (:task/uuid %)) %)
                                              (get db ::tasks []))
                                        (get-in db (task-ident task-uuid))))]
    {:db (patch-task db task-uuid #(assoc % :task/assigned-to optimistic))
     :fx [[:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid
                      {:task/assigned-to optimistic}]]]
     :resolve {:query [{(list 'task/assign (merge {:task/uuid task-uuid}
                                                  (assignee->assign-params assignee)))
                        [:task/uuid {:task/assigned-to [:user/uuid :user/full-name :user/email]}]}]
               ;; Reassigning moves a task between assignee options, so refresh
               ;; the facet sidebar's counts (mount-guarded in ::load-facets).
               :on-success-fx (into [[:dispatch [::assign-resolved task-uuid '?body]]
                                     [:dispatch [::load-facets]]
                                     tasks-changed-fx]
                                    extra-success-fx)
               :on-error-fx [[:dispatch [::assign-failed task-uuid previous]]]}}))

(defevent-fx ::assign-failed [{:keys [db]} task-uuid previous]
  {:db (patch-task db task-uuid #(assoc % :task/assigned-to previous))
   :fx [[:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid
                    {:task/assigned-to previous}]]
        [:dispatch [:toast/error {:text "Could not assign task."}]]]})

(defevent ::assign-resolved
  [db task-uuid response]
  (patch-task db task-uuid
              #(assoc % :task/assigned-to
                      (:task/assigned-to (get response 'task/assign)))))

(defevent-fx ::update-failed [{:keys [db]} task-uuid rollback response]
  {:db (patch-task db task-uuid
                   (fn [task]
                     (reduce-kv (fn [t k v]
                                  (if (nil? v) (dissoc t k) (assoc t k v)))
                                task rollback)))
   :fx [[:dispatch [:toast/error {:text (error-text response)}]]]})

;; ---------------------------------------------------------------------------
;; Dropped linked entities (contacts / notes / tasks). `::save-edit` dispatches
;; these for the chips the composer dropped; its staged patch already hides
;; them, so each just detaches through its `task/remove-*` mutation and
;; re-resolves the detail page's entity-store copy to server truth.
;; ---------------------------------------------------------------------------

(defn- links-refresh-fx
  "Re-resolve the detail page's entity-store copy of the task after a link
   mutation, so its `:task/contacts` / `:task/notes` / `:task/tasks` catch up.
   Guarded on the task being in the store — links only surface on the detail
   page, whose route controller seeded it there."
  [db task-uuid]
  (cond-> []
    (get-in db (task-ident task-uuid))
    (conj [:dispatch [:entity/resolve {:task/uuid task-uuid}]])))

;; Spaces reuse the generic space mutations against the task, then re-resolve it
;; so its `:entity/spaces` chips catch up. `::save-edit` dispatches these for the
;; spaces the composer added/dropped.
(defevent-fx ::add-to-space [{:keys [db]} task-uuid space-uuid]
  {:resolve {:query [(list 'space/add-item {:space/uuid space-uuid :task/uuid task-uuid})]
             :on-success-fx (conj (links-refresh-fx db task-uuid) tasks-changed-fx)
             :on-error-fx [[:dispatch [:toast/error {:text "Could not add to space. Try again."}]]]}})

(defevent-fx ::remove-from-space [{:keys [db]} task-uuid space-uuid]
  {:resolve {:query [(list 'space/remove-item {:space/uuid space-uuid :task/uuid task-uuid})]
             :on-success-fx (conj (links-refresh-fx db task-uuid) tasks-changed-fx)
             :on-error-fx [[:dispatch [:toast/error {:text "Could not remove from space. Try again."}]]]}})

(defevent-fx ::remove-contact [{:keys [db]} {task-uuid :task/uuid contact-uuid :contact/uuid}]
  {:resolve {:query [{(list 'task/remove-contact {:task/uuid task-uuid
                                                  :contact/uuid contact-uuid})
                      [:task/uuid {:task/contacts [:contact/uuid :contact/full-name :contact/organisation?]}]}]
             :on-success-fx (conj (links-refresh-fx db task-uuid) tasks-changed-fx)
             :on-error-fx [[:dispatch [:toast/error {:text "Could not remove contact. Try again."}]]]}})

(defevent-fx ::remove-note-link [{:keys [db]} {task-uuid :task/uuid note-uuid :note/uuid}]
  {:resolve {:query [{(list 'task/remove-note {:task/uuid task-uuid
                                               :note/uuid note-uuid})
                      [:task/uuid {:task/notes [:note/uuid :note/text]}]}]
             :on-success-fx (conj (links-refresh-fx db task-uuid) tasks-changed-fx)
             :on-error-fx [[:dispatch [:toast/error {:text "Could not remove linked note. Try again."}]]]}})

;; The linked task's uuid rides as `:task/linked-uuid` (both entities are tasks;
;; the backend's `task/remove-task` disambiguates the same way).
(defevent-fx ::remove-task-link [{:keys [db]} {task-uuid :task/uuid linked-uuid :task/linked-uuid}]
  {:resolve {:query [{(list 'task/remove-task {:task/uuid task-uuid
                                               :task/linked-uuid linked-uuid})
                      [:task/uuid {:task/tasks [:task/uuid :task/title]}]}]
             :on-success-fx (conj (links-refresh-fx db task-uuid) tasks-changed-fx)
             :on-error-fx [[:dispatch [:toast/error {:text "Could not remove linked task. Try again."}]]]}})

(defevent ::clear [db _]
  (dissoc db ::tasks ::load-result ::highlighted-uuid))

(defsub ::list [db]
  (get db ::tasks []))

(defsub ::loaded? [db]
  (contains? db ::tasks))

(defsub ::highlighted? [db task-uuid]
  (= task-uuid (::highlighted-uuid db)))

;; Today's completions carried in the loaded slice (Home only — the page loads
;; open tasks only), oldest-first for the progress timeline. Only the tasks
;; assigned to the user: the slice also holds ones they own but assigned to
;; someone else.
(defsub ::completed-today
  [current-user <- [:auth/identity]
   tasks <- [::list]]
  (->> tasks
       ;; Only actual completions — an open task's `(js/Date. nil)` → NaN would
       ;; poison the Progress timeline's window math.
       (filter :task/completed-at)
       (filter #(assignee-me? current-user (:task/assigned-to %)))
       (sort-by #(.getTime (js/Date. (:task/completed-at %))))))

;; Open tasks in the loaded slice whose deadline lands on today, earliest first.
;; Home's Progress card shows these as pills beside the day's all-day calendar
;; events. Scope follows the slice: `:user/current-tasks` is owned-or-assigned,
;; so a task only shared with the user doesn't appear — the same scope every
;; other card on the page uses.
(defsub ::deadlines-today
  [tasks <- [::list]]
  (->> tasks
       (filter deadline/due-today?)
       (sort-by (juxt #(.getTime (:task/deadline-at %)) :task/title))))

(defn- sort-bucket
  "Newest first by creation time."
  [tasks]
  (sort-by #(.getTime (js/Date. (:task/created-at %))) > tasks))

(defn bucket-tasks
  "Group tasks into :none / :now / :next / :later by urgency, each newest-first.
   Tasks with no urgency land in :none (see `task-bucket`)."
  [tasks]
  (let [grouped (group-by task-bucket tasks)]
    {:none  (sort-bucket (:none grouped))
     :now   (sort-bucket (:now grouped))
     :next  (sort-bucket (:next grouped))
     :later (sort-bucket (:later grouped))}))

(defsub ::buckets
  [tasks <- [::list]]
  (bucket-tasks tasks))

(def bucket-tabs
  "Ordered tabs over `::buckets`: bucket id and display label. None leads, the
   way the ＋ pill's menu lists it — the tasks nobody has placed yet, above the
   lanes they get placed into."
  [{:id :none :label "None"}
   {:id :now :label "Now"}
   {:id :next :label "Next"}
   {:id :later :label "Later"}])

(def bucket-ids
  "Every tab's id."
  (into #{} (map :id) bucket-tabs))

;; ---------------------------------------------------------------------------
;; Board facets — option list + SMART counts for the kanban assignee filter
;; sidebar (`hai.pathom.tasks/user-task-facets`, [HAI-006-01]). Each dimension's
;; counts respect the board's live search + the OTHER dimensions' filters, sent
;; as query params. Mirrors `hai.notes.data`'s facets section. Loaded on the
;; sidebar's mount ([HAI-006-03]), after task mutations, and after every filter
;; or (debounced) search change ([HAI-006-04]).
;; ---------------------------------------------------------------------------

(def ^:private facets-eql
  [:facet/total
   :facet/unassigned-count
   :facet/untagged-count
   {:facet/tags [:tag/name :facet/count]}
   {:facet/assignees [:user/uuid :user/full-name :facet/count]}])

(defn- live-filters
  "The board's active filter values, read from the filters SUB-form's fields —
   not the root form's `:table/filters` map. A sub-field write lands in the
   sub-form's field state synchronously but reaches the root map only through a
   chain of queued events, so a facet-click handler dispatching [[::load-facets]]
   right after `set-value!` would read a stale root. Degrades to `{}` while the
   board declares no filter columns ([HAI-006-04]) and the sub-form is unmounted:
   `get-in` returns nil and `keep` yields nothing rather than throwing."
  [db]
  (into {}
        (keep (fn [[attr {current-value :form.field/current-value}]]
                (when (some? current-value)
                  [attr current-value])))
        (get-in db [:forms [board-collection-id :table/filters] :form/fields])))

;; Self-guarded on the board being mounted, unlike notes' unconditional wall
;; reload: task mutations also fire from Home, the detail page, and the mobile
;; add-sheet, where no board (and no facets sidebar) is rendered — so this is
;; the facets analog of the board's own `:table/resolve-if-mounted`. On the
;; sidebar mount and on filter/search changes the board is always present, so
;; the guard is a no-op there.
(defevent-fx ::load-facets
  [{:keys [db]} _]
  (when (get-in db [:collections board-collection-id])
    (let [active (live-filters db)
          search (get-in db [:forms board-collection-id :form/fields
                             :table/search :form.field/current-value])
          params (cond-> {}
                   (seq active) (assoc :filters active)
                   (some-> search str/trim not-empty) (assoc :search search))]
      {:resolve {:query        [(if (seq params)
                                  (list {:user/task-facets facets-eql} params)
                                  {:user/task-facets facets-eql})]
                 :transform-fn :user/task-facets
                 :store-in     [::facets]}})))

;; Search keystrokes funnel through this so the counts re-query once typing
;; pauses — the same cadence as the board's own debounced resolve ([HAI-006-04]).
(defevent-fx ::load-facets-debounced
  [_ _]
  {:debounce/dispatch {:id ::load-facets
                       :event [::load-facets]
                       :duration 500}})

(defsub ::facets [db]
  (get db ::facets))
